import React from "react";
import Button from "react-bootstrap/Button";
import toast from "react-hot-toast";
import axiosInstance from "../../api/axiosInstance";
import S3progress from "./s3progress";
import { useS3context } from "../../store/s3Context";

const Fileurl = ({
  fromConservationDetail = false,
  updateS3,
  setUpdateS3,
  update,
  setUpdate,
  detail = false,
  accession_number,
  setS3modal,
  fromConservation = false,
  refreshHandler,
}) => {
  // const [loading, setLoading] = useState(false)
  // const [progress, setProgress] = useState(0)
  // const [timeRemaining, setTimeRemaining] = useState(null);
  // const [uploadSpeed, setUploadSpeed] = useState(null);
  // const [uploadedFiles, setUploadedFiles] = useState(0);
  // const [totalSize, setTotalSize] = useState(0);
  // const [uploadedSize, setUploadedSize] = useState(0);
  const {
    s3loading,
    setS3Loading,
    progress,
    setProgress,
    timeRemaining,
    setTimeRemaining,
    uploadSpeed,
    setUploadSpeed,
    uploadedFiles,
    setUploadedFiles,
    totalSize,
    setTotalSize,
    uploadedSize,
    setUploadedSize,
  } = useS3context();

  const userData = JSON.parse(localStorage.getItem("user_info"));

  // const readAsDataURL = (file) => {

  //     return new Promise((resolve, reject) => {
  //         const reader = new FileReader();

  //         reader.onload = () => {
  //             resolve(reader.result); // base64 data string
  //         };

  //         reader.onerror = reject;

  //         reader.readAsDataURL(file);
  //     });
  // };

  // METHOD 1: Bracket Notation FormData (explicit nested structure)
  function createNestedFormData(structure, imageFields) {
    const formData = new FormData();

    // Create a map for easy file lookup by UUID key
    const fileMap = new Map();
    imageFields.forEach(({ key, file }) => {
      fileMap.set(key, file);
    });

    // Basic structure fields
    formData.append("museum", structure.museum || "");
    formData.append("folder_name", structure.folder_name);
    formData.append("total_files", structure.total_files);
    formData.append("image_type", structure.image_type);

    // Nested contents structure
    structure.contents.forEach((content, contentIndex) => {
      // Each content item
      formData.append(
        `contents[${contentIndex}]accession_number`,
        content.accession_number
      );

      // Each file within the content
      content.folder_content.forEach((fileData, fileIndex) => {
        // Get the actual file using the UUID key
        const actualFile = fileMap.get(fileData.image);

        if (actualFile) {
          // Append the actual binary file directly to the image field
          formData.append(
            `contents[${contentIndex}]folder_content[${fileIndex}]image`,
            actualFile
          );
        } else {
          console.warn(`⚠️ File not found for key: ${fileData.image}`);
        }

        formData.append(
          `contents[${contentIndex}]folder_content[${fileIndex}]file_name`,
          fileData.file_name
        );
        formData.append(
          `contents[${contentIndex}]folder_content[${fileIndex}]full_path`,
          fileData.full_path
        );
        // formData.append(`contents[${contentIndex}]folder_content[${fileIndex}]file_size`, fileData.file_size);
        // formData.append(`contents[${contentIndex}]folder_content[${fileIndex}]file_type`, fileData.file_type);
      });
    });

    // Debug log
    console.log("📦 Nested FormData structure created:");
    for (let [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(`📁 ${key}: File(${value.name}, ${value.size} bytes)`);
      } else {
        console.log(`📝 ${key}: ${value}`);
      }
    }

    return formData;
  }

  // METHOD 2: JSON + Files FormData (cleaner, recommended)
  function createJSONFormData(structure, imageFields) {
    const formData = new FormData();

    // Send entire structure as JSON
    formData.append("structure", JSON.stringify(structure));

    // Add files separately
    imageFields.forEach(({ key, file }) => {
      formData.append(key, file);
    });

    return formData;
  }

  // METHOD 3: Hybrid approach (best of both worlds)
  function createHybridFormData(structure, imageFields) {
    const formData = new FormData();

    // Basic info as separate fields
    formData.append("museum", structure.museum || "");
    formData.append("folder_name", structure.folder_name);
    formData.append("total_folders", structure.contents.length);
    formData.append("total_files", imageFields.length);

    // Complex nested structure as JSON
    formData.append("contents", JSON.stringify(structure.contents));

    // Files with their keys
    imageFields.forEach(({ key, file }) => {
      formData.append(key, file);
    });

    return formData;
  }

  const handleFolderSelectWithOptions = async (event, method = "nested") => {
    setS3Loading(true);
    setProgress(0);
    setTimeRemaining(null);
    setUploadSpeed(null);
    setUploadedFiles(0);
    setUploadedSize(0);
    const startTime = Date.now();

    const allFiles = Array.from(event.target.files);
    if (!allFiles.length) return;

    // ============ FILE TYPE VALIDATION ============
    const isValidFile = (file) => {
      const fileName = file.name.toLowerCase();
      const isImage = file.type.startsWith("image/");
      const isGLB = fileName.endsWith(".glb");

      // If fromConservation is true, only allow images (no GLB files)
      if (fromConservation) {
        return isImage;
      }

      // Otherwise, allow both images and GLB files
      return isImage || isGLB;
    };

    // Filter valid files and track rejected ones
    const files = allFiles.filter(isValidFile);
    const rejectedCount = allFiles.length - files.length;

    // if (rejectedCount > 0) {
    //   toast.error(`⚠️ ${rejectedCount} non-image/non-GLB file(s) skipped`);
    // }

    if (!files.length) {
      const expectedType = fromConservation ? "image" : "image or GLB";
      toast.error(`No valid ${expectedType} files found!`);
      setS3Loading(false);
      setS3modal(false);
      event.target.value = "";
      return;
    }

    // ============ DETERMINE TARGET FOLDER BASED ON fromconservation ============
    const targetFolderName = fromConservation
      ? "Technical Images"
      : "Post Processed";
    const targetFolderNormalized = fromConservation
      ? "technicalimages"
      : "postprocessed";

    // ============ HELPER FUNCTION: Check if folder name matches target ============
    const isTargetFolder = (folderName) => {
      const normalized = folderName.toLowerCase().replace(/[\s-_]/g, "");
      return normalized === targetFolderNormalized;
    };

    // ============ FILTER FILES: Only keep files from target folder ============
    const targetFiles = files.filter((file) => {
      const parts = file.webkitRelativePath.split("/");
      return parts.some((part) => isTargetFolder(part));
    });

    if (targetFiles.length === 0) {
      toast.error(`No files found in '${targetFolderName}' folder!`);
      console.warn(`No ${targetFolderName} folder found in selection`);
      setS3Loading(false);
      setS3modal(false);
      event.target.value = "";
      return;
    }

    const FILE_LIMIT = 500;
    if (targetFiles.length > FILE_LIMIT) {
      toast.error(`Maximum allowed: ${FILE_LIMIT} files!`);
      setS3Loading(false);
      setS3modal(false);
      event.target.value = "";
      return;
    }

    const totalBytes = targetFiles.reduce(
      (total, file) => total + file.size,
      0
    );
    setTotalSize(totalBytes);

    let totalFileCount = 0;
    const firstFilePath = targetFiles[0].webkitRelativePath;
    const firstFileParts = firstFilePath.split("/");

    // ============ DETECT FOLDER STRUCTURE ============
    const targetFolderIndex = firstFileParts.findIndex((part) =>
      isTargetFolder(part)
    );

    if (targetFolderIndex === -1) {
      toast.error(`No ${targetFolderName} folder found!`);
      setS3Loading(false);
      setS3modal(false);
      event.target.value = "";
      return;
    }

    // Determine structure type
    let structure;
    const subfolderMap = new Map();
    const imageFields = [];
    let isDirectSelection = false;
    let isMultipleAccessions = false;

    // Check structure type
    if (targetFolderIndex === 0 && firstFileParts.length === 2) {
      // Case 1: Technical Images/image.jpg OR Post Processed/image.jpg (direct selection)
      isDirectSelection = true;

      if (detail && accession_number) {
      } else {
        toast.error(
          `Please open an accession detail page before uploading ${targetFolderName}`
        );
        setS3Loading(false);
        setS3modal(false);
        event.target.value = "";
        return;
      }
    } else if (targetFolderIndex === 1 && firstFileParts.length === 3) {
      // Case 2: accession_number/Technical Images/image.jpg (single accession)
      isDirectSelection = false;
      isMultipleAccessions = false;

      const accessionFromPath = firstFileParts[0];

      // Validate against current accession if in detail view
      if (detail && accession_number) {
        if (accessionFromPath !== accession_number) {
          toast.error("Selected folder doesn't match current accession number");
          setS3Loading(false);
          setS3modal(false);
          event.target.value = "";
          return;
        }
      }
    } else if (targetFolderIndex === 2 && firstFileParts.length === 4) {
      // Case 3: main_folder/accession_number/Technical Images/image.jpg (multiple accessions)
      isDirectSelection = false;
      isMultipleAccessions = true;
    } else {
      toast.error("Unsupported folder structure!");
      setS3Loading(false);
      setS3modal(false);
      event.target.value = "";
      return;
    }

    // ============ PROCESS FILES BASED ON STRUCTURE ============

    if (isDirectSelection) {
      // Case 1: Direct selection - Technical Images/image.jpg OR Post Processed/image.jpg
      const folderName = accession_number;

      structure = {
        museum: userData?.museum,
        folder_name: folderName,
        total_files: "",
        image_type: fromConservation ? "technical_images" : "digital_images",
        contents: [],
      };

      for (const file of targetFiles) {
        const relativePath = file.webkitRelativePath;
        const parts = relativePath.split("/");

        if (parts.length < 2) continue;

        totalFileCount++;
        const fieldKey = `image_${crypto.randomUUID()}`;

        const fileData = {
          image: fieldKey,
          file_name: file.name,
          full_path: relativePath,
        };

        if (!subfolderMap.has(folderName)) {
          subfolderMap.set(folderName, []);
        }

        subfolderMap.get(folderName).push(fileData);
        imageFields.push({ key: fieldKey, file });
      }

      structure.contents.push({
        accession_number: folderName,
        folder_content: subfolderMap.get(folderName),
      });
    } else if (!isMultipleAccessions) {
      // Case 2: Single accession - accession_number/Technical Images/image.jpg
      const accessionNumber = firstFileParts[0];

      if (detail && accession_number && accessionNumber !== accession_number) {
        toast.error("Select the same folder");
        setS3Loading(false);
        setS3modal(false);
        event.target.value = "";
        return;
      }

      structure = {
        museum: userData?.museum,
        folder_name: accessionNumber,
        total_files: "",
        image_type: fromConservation ? "technical_images" : "digital_images",
        contents: [],
      };

      for (const file of targetFiles) {
        const relativePath = file.webkitRelativePath;
        const parts = relativePath.split("/");

        if (parts.length < 3) continue;

        totalFileCount++;
        const fieldKey = `image_${crypto.randomUUID()}`;

        const fileData = {
          image: fieldKey,
          file_name: file.name,
          full_path: relativePath,
        };

        if (!subfolderMap.has(accessionNumber)) {
          subfolderMap.set(accessionNumber, []);
        }

        subfolderMap.get(accessionNumber).push(fileData);
        imageFields.push({ key: fieldKey, file });
      }

      structure.contents.push({
        accession_number: accessionNumber,
        folder_content: subfolderMap.get(accessionNumber),
      });
    } else {
      // Case 3: Multiple accessions - main_folder/accession_number/Technical Images/image.jpg
      const mainFolderName = firstFileParts[0];

      structure = {
        museum: userData?.museum,
        folder_name: mainFolderName,
        total_files: "",
        image_type: fromConservation ? "technical_images" : "digital_images",
        contents: [],
      };

      for (const file of targetFiles) {
        const relativePath = file.webkitRelativePath;
        const parts = relativePath.split("/");

        if (parts.length < 4) continue;

        const accessionNumber = parts[1]; // Extract accession number
        const targetIdx = parts.findIndex((part) => isTargetFolder(part));

        if (targetIdx === -1) continue;

        totalFileCount++;
        const fieldKey = `image_${crypto.randomUUID()}`;

        const fileData = {
          image: fieldKey,
          file_name: file.name,
          full_path: relativePath,
        };

        if (!subfolderMap.has(accessionNumber)) {
          subfolderMap.set(accessionNumber, []);
        }

        subfolderMap.get(accessionNumber).push(fileData);
        imageFields.push({ key: fieldKey, file });
      }

      // Build structure - accession_number is subfolder name
      for (const [accessionNumber, folder_content] of subfolderMap.entries()) {
        structure.contents.push({
          accession_number: accessionNumber,
          folder_content,
        });
      }
    }

    // Validate that files were found
    if (totalFileCount === 0) {
      const expectedType = fromConservation ? "image" : "image or GLB";
      toast.error(
        `No valid ${expectedType} files found in ${targetFolderName} folder!`
      );
      setS3Loading(false);
      setS3modal(false);
      event.target.value = "";
      return;
    }

    structure.total_files = totalFileCount;

    // Choose FormData creation method
    let formData;
    switch (method) {
      case "nested":
        formData = createNestedFormData(structure, imageFields);
        break;
      case "hybrid":
        formData = createHybridFormData(structure, imageFields);
        break;
      case "json":
      default:
        formData = createJSONFormData(structure, imageFields);
        break;
    }

    const uploadWithProgressAlternative = () => {
      return new Promise((resolve) => {
        const config = {
          method: "post",
          url: "s3/sync/sync-folder",
          data: formData,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.lengthComputable) {
              const percentComplete = Math.round(
                (progressEvent.loaded / progressEvent.total) * 100
              );
              setProgress(percentComplete);
              setUploadedSize(progressEvent.loaded);

              const elapsedTime = (Date.now() - startTime) / 1000;
              const uploadSpeed = progressEvent.loaded / elapsedTime;
              const remainingBytes = progressEvent.total - progressEvent.loaded;
              const estimatedTimeRemaining = remainingBytes / uploadSpeed;

              setTimeRemaining(estimatedTimeRemaining);
              setUploadSpeed(uploadSpeed / (1024 * 1024));

              const estimatedFilesUploaded = Math.floor(
                (progressEvent.loaded / progressEvent.total) * totalFileCount
              );
              setUploadedFiles(estimatedFilesUploaded);
            }
          },
        };

        axiosInstance(config)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            console.error("Upload error:", error);
            resolve({
              status: false,
              error: error.response?.data?.message || "Upload failed",
            });
          });
      });
    };

    try {
      const response = await uploadWithProgressAlternative();

      if (response?.status) {
        setProgress(100);
        setUploadedFiles(totalFileCount);
        setUploadedSize(totalBytes);
        setTimeRemaining(0);
        if (detail) {
          if (fromConservationDetail) {
            setUpdateS3(!updateS3);
          } else {
            setUpdate(!update);
          }
        }

        toast.success(`Images synced with cloud server`);
        if (typeof refreshHandler === "function") {
          refreshHandler();
        }

        setTimeout(() => {
          setS3Loading(false);
          setS3modal(false);
          // Reset states
          setProgress(0);
          setTimeRemaining(null);
          setUploadSpeed(null);
          setUploadedFiles(0);
          setUploadedSize(0);

          // Reset file input
          event.target.value = "";
        }, 1000);
      } else {
        setS3Loading(false);
        setS3modal(false);
        toast.error(response?.error);
      }
    } catch (error) {
      setS3Loading(false);
      setS3modal(false);
      console.error("Unexpected error:", error);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        justifyContent: "center",
      }}
    >
      {s3loading ? (
        <div style={{ marginBottom: "9px" }}>
          {s3loading && (
            <S3progress
              progress={progress}
              timeRemaining={timeRemaining}
              uploadSpeed={uploadSpeed}
              uploadedFiles={uploadedFiles}
              totalSize={totalSize}
              uploadedSize={uploadedSize}
            />
          )}
        </div>
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "19px",
              }}
            >
              <p>
                {" "}
                {detail
                  ? `Select the "${accession_number}" folder!`
                  : "Choose the folder where you want to sync your data!"}
              </p>
              <Button
                onClick={() => document.querySelector(".sync-input").click()}
                className="sync-container"
              >
                Select
              </Button>
            </div>
          </div>
          <input
            style={{
              display: "none",
            }}
            type="file"
            webkitdirectory="true"
            directory=""
            multiple
            className="sync-input"
            onChange={handleFolderSelectWithOptions}
          />
        </>
      )}
    </div>
  );
};

export default Fileurl;
